import React from 'react';
import { seoConfig } from '../../config/seo';
interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  canonicalUrl?: string;
  noindex?: boolean;
  structuredData?: object;
}
/**
 * SEO Head component for meta tags and structured data
 * Handles all SEO-related head elements
 *
 * @example
 * <SEOHead
 *   title="Pricing Plans"
 *   description="Choose the perfect plan for your needs"
 * />
 */
export function SEOHead({
  title,
  description = seoConfig.defaultDescription,
  keywords = seoConfig.keywords,
  ogImage = seoConfig.ogImage,
  ogType = 'website',
  canonicalUrl,
  noindex = false,
  structuredData
}: SEOHeadProps) {
  const fullTitle = title ? seoConfig.titleTemplate.replace('%s', title) : seoConfig.defaultTitle;
  const canonical = canonicalUrl || seoConfig.siteUrl;
  return <>
      {/* Primary Meta Tags */}
      <title data-id="element-2112">{fullTitle}</title>
      <meta name="title" content={fullTitle} data-id="element-2113" />
      <meta name="description" content={description} data-id="element-2114" />
      <meta name="keywords" content={keywords.join(', ')} data-id="element-2115" />

      {/* Robots */}
      {noindex ? <meta name="robots" content="noindex, nofollow" data-id="element-2116" /> : <meta name="robots" content="index, follow" data-id="element-2117" />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonical} data-id="element-2118" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} data-id="element-2119" />
      <meta property="og:url" content={canonical} data-id="element-2120" />
      <meta property="og:title" content={fullTitle} data-id="element-2121" />
      <meta property="og:description" content={description} data-id="element-2122" />
      <meta property="og:image" content={ogImage} data-id="element-2123" />
      <meta property="og:image:width" content={String(seoConfig.ogImageWidth)} data-id="element-2124" />
      <meta property="og:image:height" content={String(seoConfig.ogImageHeight)} data-id="element-2125" />
      <meta property="og:site_name" content={seoConfig.siteName} data-id="element-2126" />

      {/* Twitter */}
      <meta property="twitter:card" content={seoConfig.twitterCard} data-id="element-2127" />
      <meta property="twitter:url" content={canonical} data-id="element-2128" />
      <meta property="twitter:title" content={fullTitle} data-id="element-2129" />
      <meta property="twitter:description" content={description} data-id="element-2130" />
      <meta property="twitter:image" content={seoConfig.twitterImage} data-id="element-2131" />
      {seoConfig.social.twitter && <meta property="twitter:site" content={seoConfig.social.twitter} data-id="element-2132" />}

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" data-id="element-2133" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" data-id="element-2134" />
      <meta name="language" content="English" data-id="element-2135" />
      <meta name="author" content={seoConfig.siteName} data-id="element-2136" />

      {/* Structured Data */}
      {structuredData && <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(structuredData)
    }} data-id="element-2137" />}
    </>;
}